import {
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Modal,
    Paper,
    Select,
    Slide,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Container } from '../../components'
import { User } from '../../models'
import { typographyStyle } from '../../theme/typographyStyle'
import { useMultiSelectStyles } from '../../styles'
import {
    assignSubSlice,
    fetchEnabledServicesAtApartment,
} from './subAssignRulesApi'

export interface SliceAssignOption {
    id: number
    name: string
}

export interface SliceAssignModalProps {
    isOpen: boolean
    onClose: () => void
    apartmentList: SliceAssignOption[]
    userList: User[]
    userListLoading?: boolean
    submitting?: boolean
    alwaysSaveRule?: boolean
    onSuccess: () => void
}

export const SliceAssignModal = ({
    isOpen,
    onClose,
    apartmentList,
    userList,
    userListLoading = false,
    submitting = false,
    alwaysSaveRule = false,
    onSuccess,
}: SliceAssignModalProps) => {
    const theme = useTheme()
    const classes = useMultiSelectStyles()
    const [apartmentId, setApartmentId] = useState(-1)
    const [serviceId, setServiceId] = useState(-1)
    const [userId, setUserId] = useState(-1)
    const [saveRule, setSaveRule] = useState(true)
    const [busy, setBusy] = useState(false)
    const [serviceList, setServiceList] = useState<SliceAssignOption[]>([])
    const [servicesLoading, setServicesLoading] = useState(false)

    useEffect(() => {
        if (!isOpen) {
            return
        }
        setApartmentId(-1)
        setServiceId(-1)
        setUserId(-1)
        setSaveRule(true)
        setServiceList([])
    }, [isOpen, alwaysSaveRule])

    useEffect(() => {
        if (!isOpen || apartmentId <= 0) {
            setServiceList([])
            setServicesLoading(false)
            return
        }
        let cancelled = false
        setServicesLoading(true)
        setServiceId(-1)
        void fetchEnabledServicesAtApartment(apartmentId)
            .then((rows) => {
                if (!cancelled) {
                    setServiceList(rows)
                }
            })
            .catch(() => {
                if (!cancelled) {
                    toast.error('Could not load services for that property.')
                    setServiceList([])
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setServicesLoading(false)
                }
            })
        return () => {
            cancelled = true
        }
    }, [isOpen, apartmentId])

    const sortedUsers = useMemo(
        () =>
            [...userList].sort((a, b) =>
                (a.name ?? '').localeCompare(b.name ?? '', undefined, {
                    sensitivity: 'base',
                }),
            ),
        [userList],
    )

    const sortedApts = useMemo(
        () =>
            [...apartmentList].sort((a, b) =>
                a.name.localeCompare(b.name, undefined, {
                    sensitivity: 'base',
                }),
            ),
        [apartmentList],
    )

    const sortedServices = useMemo(
        () =>
            [...serviceList].sort((a, b) =>
                a.name.localeCompare(b.name, undefined, {
                    sensitivity: 'base',
                }),
            ),
        [serviceList],
    )

    const canSubmit =
        apartmentId > 0 &&
        serviceId > 0 &&
        userId > 0 &&
        !busy &&
        !submitting &&
        !servicesLoading

    const serviceSelectDisabled =
        apartmentId <= 0 || servicesLoading || busy || submitting

    const handleClose = () => {
        if (busy || submitting) {
            return
        }
        onClose()
    }

    const handleSubmit = async () => {
        if (!canSubmit) {
            return
        }
        setBusy(true)
        try {
            const res = await assignSubSlice({
                apartment_id: apartmentId,
                service_id: serviceId,
                user_id: userId,
                save_rule: alwaysSaveRule || saveRule,
            })
            const count = res.assigned_count
            const didSaveRule = alwaysSaveRule || saveRule
            if (didSaveRule) {
                toast.success(
                    count === 0
                        ? 'Rule saved. No unassigned workorders matched.'
                        : `Assigned ${count} workorder${
                              count === 1 ? '' : 's'
                          } and saved the rule.`,
                )
            } else if (count === 0) {
                toast.info(
                    'No unassigned workorders matched that property and service.',
                )
            } else {
                toast.success(
                    `Assigned ${count} workorder${count === 1 ? '' : 's'}.`,
                )
            }
            onSuccess()
            onClose()
        } catch {
            toast.error('Could not assign workorders.')
        } finally {
            setBusy(false)
        }
    }

    return (
        <Modal open={isOpen} onClose={handleClose}>
            <Slide direction="left" in={isOpen}>
                <Paper
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        height: '100%',
                        width: 420,
                        maxWidth: '100%',
                        outline: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Container
                        style={{
                            padding: theme.spacing(2),
                            alignItems: 'center',
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Typography
                            style={{
                                ...typographyStyle(theme.typography.h6),
                                fontWeight: 700,
                            }}
                        >
                            Assign by property & service
                        </Typography>
                        <div style={{ flex: 1 }} />
                        <Tooltip title="Cancel">
                            <IconButton
                                onClick={handleClose}
                                disabled={busy || submitting}
                                size="small"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Container>
                    <div
                        style={{
                            padding: theme.spacing(2),
                            overflowY: 'auto',
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: theme.spacing(2),
                        }}
                    >
                        <Typography variant="body2" color="textSecondary">
                            Assign every unassigned Assigned-status workorder
                            for a property and service to one teammate. Saving
                            the rule auto-assigns matching work when the
                            property sends more jobs.
                        </Typography>
                        <FormControl
                            className={classes.formControl}
                            variant="outlined"
                            fullWidth
                        >
                            <InputLabel id="slice-apt-label">
                                Property
                            </InputLabel>
                            <Select
                                labelId="slice-apt-label"
                                label="Property"
                                value={apartmentId}
                                onChange={(e) => {
                                    setApartmentId(Number(e.target.value))
                                    setServiceId(-1)
                                }}
                            >
                                <MenuItem value={-1}>Select property</MenuItem>
                                {sortedApts.map((apt) => (
                                    <MenuItem key={apt.id} value={apt.id}>
                                        {apt.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl
                            className={classes.formControl}
                            variant="outlined"
                            fullWidth
                        >
                            <InputLabel id="slice-svc-label">
                                Service
                            </InputLabel>
                            <Select
                                labelId="slice-svc-label"
                                label="Service"
                                value={serviceId}
                                disabled={serviceSelectDisabled}
                                onChange={(e) =>
                                    setServiceId(Number(e.target.value))
                                }
                            >
                                <MenuItem value={-1}>
                                    {apartmentId <= 0
                                        ? 'Select a property first'
                                        : servicesLoading
                                        ? 'Loading services…'
                                        : sortedServices.length === 0
                                        ? 'No services at this property'
                                        : 'Select service'}
                                </MenuItem>
                                {sortedServices.map((svc) => (
                                    <MenuItem key={svc.id} value={svc.id}>
                                        {svc.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl
                            className={classes.formControl}
                            variant="outlined"
                            fullWidth
                        >
                            <InputLabel id="slice-user-label">Sub</InputLabel>
                            <Select
                                labelId="slice-user-label"
                                label="Sub"
                                value={userId}
                                disabled={userListLoading}
                                onChange={(e) =>
                                    setUserId(Number(e.target.value))
                                }
                            >
                                <MenuItem value={-1}>Select teammate</MenuItem>
                                {sortedUsers.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>
                                        {u.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {!alwaysSaveRule && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={saveRule}
                                        onChange={(_, checked) =>
                                            setSaveRule(checked)
                                        }
                                        color="primary"
                                    />
                                }
                                label="Also save as a rule for future work"
                            />
                        )}
                    </div>
                    <Container
                        style={{
                            padding: theme.spacing(2),
                            borderTop: `1px solid ${theme.palette.divider}`,
                            justifyContent: 'flex-end',
                            gap: theme.spacing(1),
                        }}
                    >
                        <Button
                            onClick={handleClose}
                            disabled={busy || submitting}
                            style={{ textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!canSubmit}
                            onClick={() => {
                                void handleSubmit()
                            }}
                            style={{ textTransform: 'none' }}
                        >
                            {busy ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : alwaysSaveRule || saveRule ? (
                                'Assign & save rule'
                            ) : (
                                'Assign matching'
                            )}
                        </Button>
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}
